import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/format";
import { api } from "@/lib/api";
import {
  Loader2,
  ArrowRight,
  CheckCircle2,
  QrCode,
  ChefHat,
  BarChart3,
  ShieldCheck,
  X,
  KeyRound,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/common/BrandLogo";

const MAIN_IMAGE =
  "https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=1600&q=85";

const FOOD_IMAGE =
  "https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=700&q=85";

const DISH_IMAGE =
  "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=700&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const [forgotForm, setForgotForm] = useState({
    email: "",
    otp: "",
    password: "",
    confirmPassword: "",
  });

  const openForgotPassword = () => {
    setForgotError("");
    setForgotStep(1);

    setForgotForm({
      email: "",
      otp: "",
      password: "",
      confirmPassword: "",
    });

    setForgotOpen(true);
  };

  const closeForgotPassword = () => {
    if (forgotLoading) return;

    setForgotOpen(false);
    setForgotError("");
    setForgotStep(1);
  };

  const updateForgotField = (key, value) => {
    setForgotForm((prev) => ({
      ...prev,
      [key]: key === "email" ? value.toLowerCase() : value,
    }));

    setForgotError("");
  };

  const sendForgotOtp = async () => {
    const normalizedEmail = forgotForm.email.trim().toLowerCase();

    if (!normalizedEmail) {
      setForgotError("Please enter your email address.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setForgotError("Please enter a valid email address.");
      return;
    }

    try {
      setForgotLoading(true);
      setForgotError("");

      await api.forgotPassword({
        email: normalizedEmail,
      });

      setForgotForm((prev) => ({
        ...prev,
        email: normalizedEmail,
      }));

      setForgotStep(2);

      toast.success("OTP sent to your email.");
    } catch (err) {
      setForgotError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Unable to send OTP. Please try again.",
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotPassword = async () => {
    const { email, otp, password, confirmPassword } = forgotForm;

    if (!otp.trim()) {
      setForgotError("Please enter the OTP.");
      return;
    }

    if (!/^\d{6}$/.test(otp.trim())) {
      setForgotError("OTP must be 6 digits.");
      return;
    }

    if (!password) {
      setForgotError("Please enter a new password.");
      return;
    }

    if (password.length < 6) {
      setForgotError("Password must be at least 6 characters.");
      return;
    }

    if (!confirmPassword) {
      setForgotError("Please confirm your password.");
      return;
    }

    if (password !== confirmPassword) {
      setForgotError("Passwords do not match.");
      return;
    }

    try {
      setForgotLoading(true);
      setForgotError("");

      await api.resetPassword({
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        password,
      });

      toast.success("Password updated successfully.");

      setForgotOpen(false);
      setForgotStep(1);

      setForgotForm({
        email: "",
        otp: "",
        password: "",
        confirmPassword: "",
      });

      setPassword("");
    } catch (err) {
      setForgotError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Unable to reset password. Please check the OTP and try again.",
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();

    setError("");

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError("Please enter your email address.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setLoading(true);

    try {
      const u = await login(normalizedEmail, password);

      toast.success(`Welcome back, ${u.name?.split(" ")[0] || "back"}`);
      navigate("/app");
    } catch (err) {
      setError(
        formatApiError(err.response?.data?.detail) ||
          err.message ||
          "Unable to sign in. Please check your credentials and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white overflow-hidden">
      <style>{`
        @keyframes floatSlow {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-12px);
          }
        }

        @keyframes floatReverse {
          0%, 100% {
            transform: translateY(-4px);
          }
          50% {
            transform: translateY(10px);
          }
        }

        @keyframes pulseGlow {
          0%, 100% {
            box-shadow: 0 0 0 rgba(249, 115, 22, 0);
          }
          50% {
            box-shadow: 0 0 35px rgba(249, 115, 22, 0.22);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(18px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-120%);
          }
          100% {
            transform: translateX(120%);
          }
        }

        .login-float {
          animation: floatSlow 5s ease-in-out infinite;
        }

        .login-float-reverse {
          animation: floatReverse 6s ease-in-out infinite;
        }

        .login-glow {
          animation: pulseGlow 4s ease-in-out infinite;
        }

        .login-slide {
          animation: slideUp 0.7s ease-out both;
        }

        .login-shimmer {
          position: relative;
          overflow: hidden;
        }

        .login-shimmer::after {
          content: "";
          position: absolute;
          inset: 0;
          width: 40%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255,255,255,0.10),
            transparent
          );
          transform: translateX(-120%);
          animation: shimmer 4s ease-in-out infinite;
        }
      `}</style>

      <div className="grid min-h-screen lg:grid-cols-[1.08fr_0.92fr]">
        {/* =========================================================
            LEFT — BRAND / FOOD EXPERIENCE
        ========================================================= */}
        <section className="relative hidden overflow-hidden lg:block">
          {/* Background image */}
          <img
            src={MAIN_IMAGE}
            alt="Restaurant kitchen"
            className="absolute inset-0 h-full w-full object-cover"
          />

          {/* Premium overlays */}
          <div className="absolute inset-0 bg-black/65" />
          <div className="absolute inset-0 bg-gradient-to-br from-black via-black/35 to-orange-950/60" />

          {/* Orange atmospheric glow */}
          <div className="absolute -left-32 top-20 h-96 w-96 rounded-full bg-orange-600/20 blur-[110px]" />
          <div className="absolute bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-orange-500/10 blur-[120px]" />

          <div className="relative z-10 flex min-h-screen flex-col justify-between p-10 xl:p-14">
            {/* Logo */}
            <div className="login-slide flex items-center">
              <a
                href="/"
                className="flex items-center gap-3 px-3 py-1 rounded-2xl w-fit cursor-pointer"
              >
                <img
                  src="/assets/logo-white.png"
                  alt="StayServe"
                  className="h-12 w-auto object-contain"
                />

                <div>
                  <div className="font-display text-[18px] font-black tracking-tight leading-none">
                    <span className="text-[#fff]">Stay</span>
                    <span className="text-[#FF6B00]">Serve</span>
                  </div>

                  <div className="mt-1 text-[7px] font-bold uppercase tracking-[.20em] text-[#fff]">
                    Hospitality operations
                  </div>
                </div>
              </a>
            </div>

            {/* Main copy */}
            <div className="relative max-w-2xl mt-4">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md">
                <span className="h-2 w-2 rounded-full bg-orange-500 shadow-[0_0_12px_rgba(249,115,22,0.9)]" />
                <span className="text-xs font-medium text-stone-300">
                  Built for modern hospitality
                </span>
              </div>

              <h1 className="font-display text-5xl font-black leading-[0.98] tracking-[-0.035em] text-white xl:text-6xl">
                Run your entire
                <br />
                hospitality business
                <br />
                from <span className="text-orange-500">one screen.</span>
              </h1>

              <p className="mt-6 max-w-xl text-base leading-7 text-stone-300 xl:text-lg">
                Manage orders, tables, kitchen operations, billing, inventory,
                QR ordering and analytics — all connected in one powerful
                platform.
              </p>

              {/* Feature mini pills */}
              <div className="mt-7 flex flex-wrap gap-2">
                {[
                  [QrCode, "QR Ordering"],
                  [ChefHat, "Kitchen"],
                  [BarChart3, "Analytics"],
                  [ShieldCheck, "Secure"],
                ].map(([Icon, text]) => (
                  <div
                    key={text}
                    className="flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3.5 py-2 text-xs text-stone-300 backdrop-blur-md"
                  >
                    <Icon size={14} className="text-orange-400" />
                    {text}
                  </div>
                ))}
              </div>

              {/* Animated floating food cards */}
              <div className="pointer-events-none absolute -right-10 -top-24 hidden xl:block">
                <div className="login-float rounded-2xl border border-white/15 bg-black/45 p-2 shadow-2xl backdrop-blur-xl">
                  <img
                    src={FOOD_IMAGE}
                    alt="StayServe order preview"
                    className="h-36 w-44 rounded-xl object-cover"
                  />

                  <div className="flex items-center justify-between px-2 py-2">
                    <div>
                      <div className="text-[11px] font-bold">Order #1048</div>
                      <div className="text-[9px] text-stone-400">Preparing</div>
                    </div>

                    <div className="rounded-full bg-orange-500/20 px-2 py-1 text-[9px] font-bold text-orange-400">
                      LIVE
                    </div>
                  </div>
                </div>
              </div>

              <div className="pointer-events-none absolute -right-4 top-36 hidden xl:block">
                <div className="login-float-reverse flex items-center gap-3 rounded-2xl border border-white/10 bg-black/50 p-3 shadow-2xl backdrop-blur-xl">
                  <img
                    src={DISH_IMAGE}
                    alt="StayServe QR order preview"
                    className="h-14 w-14 rounded-xl object-cover"
                  />

                  <div>
                    <div className="text-[10px] text-stone-400">
                      New QR Order
                    </div>

                    <div className="mt-0.5 text-sm font-bold">Table 07</div>

                    <div className="mt-1 flex items-center gap-1 text-[9px] text-orange-400">
                      <CheckCircle2 size={10} />
                      Sent to kitchen
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom stats */}
              <div className="mt-10 grid max-w-xl grid-cols-3 gap-5 border-t border-white/10 pt-7">
                <div>
                  <div className="font-display text-2xl font-black text-orange-400">
                    10 min
                  </div>
                  <div className="mt-1 text-xs text-stone-500">To set up</div>
                </div>

                <div>
                  <div className="font-display text-xl font-black text-orange-400 sm:text-2xl">
                    Soon
                  </div>
                  <div className="mt-1 text-xs text-stone-500">
                    Guest mobile app
                  </div>
                </div>

                <div>
                  <div className="font-display text-2xl font-black text-orange-400">
                    100%
                  </div>
                  <div className="mt-1 text-xs text-stone-500">
                    Offline-ready POS
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom brand */}
            <div className="flex items-center justify-between border-t border-white/10 pt-5 text-xs text-stone-500 mt-8">
              <span>
                Powered by{" "}
                <span className="font-semibold text-stone-300">
                  SSQUARE LOGIC
                </span>
              </span>

              <span>© 2026 StayServe</span>
            </div>
          </div>
        </section>

        {/* =========================================================
            RIGHT — LOGIN FORM
        ========================================================= */}
        <section className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#faf9f7] px-5 py-10 text-stone-900 sm:px-10">
          {/* Mobile decorative background */}
          <div className="absolute -right-32 -top-32 h-80 w-80 rounded-full bg-orange-200/40 blur-3xl lg:hidden" />
          <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-orange-100/50 blur-3xl lg:hidden" />

          <div className="relative z-10 w-full max-w-md login-slide">
            {/* Mobile logo */}
            <div className="mb-10 flex items-center justify-center lg:hidden">
              <BrandLogo
                className="h-12 w-auto max-w-[260px] object-contain"
                alt="StayServe"
              />
            </div>

            {/* Heading */}
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-orange-600">
                <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                Secure workspace
              </div>

              <h2 className="font-display text-4xl font-black tracking-[-0.03em] text-stone-950">
                Welcome back.
              </h2>

              <p className="mt-2 text-sm leading-6 text-stone-500">
                Sign in to manage your restaurant and hospitality operations.
              </p>
            </div>

            {/* Login card */}
            <div className="login-glow mt-8 rounded-[28px] border border-stone-200 bg-white p-6 shadow-[0_20px_70px_rgba(0,0,0,0.08)] sm:p-7">
              <form
                onSubmit={submit}
                className="space-y-5"
                noValidate
                aria-label="StayServe login form"
              >
                {/* Email */}
                <div>
                  <Label
                    htmlFor="email"
                    className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500"
                  >
                    Email address
                  </Label>

                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="mt-2 h-12 rounded-xl border-stone-200 bg-stone-50/60 px-4 text-sm transition-all focus:border-orange-400 focus:bg-white focus:ring-orange-100"
                    data-testid="login-email"
                  />
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label
                      htmlFor="password"
                      className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500"
                    >
                      Password
                    </Label>

                    <button
                      type="button"
                      onClick={openForgotPassword}
                      className="text-[11px] font-semibold text-orange-500 transition-colors hover:text-orange-600"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="mt-2 h-12 rounded-xl border-stone-200 bg-stone-50/60 px-4 text-sm transition-all focus:border-orange-400 focus:bg-white focus:ring-orange-100"
                    data-testid="login-password"
                  />
                </div>

                {/* Error */}
                {error && (
                  <div
                    className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600"
                    role="alert"
                    aria-live="polite"
                    data-testid="login-error"
                  >
                    {error}
                  </div>
                )}

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  aria-busy={loading}
                  data-testid="login-submit"
                  className="login-shimmer relative h-12 w-full rounded-xl bg-orange-500 text-[14px] font-bold text-white shadow-[0_8px_25px_rgba(249,115,22,0.28)] transition-all hover:bg-orange-600 hover:shadow-[0_12px_30px_rgba(249,115,22,0.35)]"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <>
                      Sign in
                      <ArrowRight size={17} className="ml-2" />
                    </>
                  )}
                </Button>
              </form>

              {/* Security note */}
              <div className="mt-5 flex items-center justify-center gap-2 text-[10px] font-medium text-stone-400">
                <ShieldCheck size={13} className="text-green-500" />
                Your workspace is protected with secure authentication
              </div>
            </div>

            {/* Get started link */}
            <div className="mt-7 text-center">
              <p className="text-sm text-stone-500">
                New to StayServe?{" "}
                <Link
                  to="/"
                  className="font-semibold text-orange-500 hover:text-orange-600 transition-colors"
                >
                  See plans &amp; get started
                </Link>
              </p>
            </div>

            {/* Footer */}
            <div className="mt-6 text-center text-[10px] leading-5 text-stone-400">
              By continuing, you agree to our{" "}
              <Link
                to="/terms-and-conditions"
                className="cursor-pointer font-medium text-stone-600 hover:text-orange-500"
              >
                Terms
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy-policy"
                className="cursor-pointer font-medium text-stone-600 hover:text-orange-500"
              >
                Privacy Policy
              </Link>
              .
              <div className="mt-2">
                Designed &amp; developed by{" "}
                <a
                  href="https://ssquarelogic.com"
                  target="_blank"
                  rel="noreferrer"
                  className="font-bold text-stone-600 hover:text-orange-500"
                >
                  SSQUARE LOGIC
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
      {forgotOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              closeForgotPassword();
            }
          }}
        >
          <div
            className="relative z-[101] w-full max-w-md pointer-events-auto overflow-hidden rounded-[28px] border border-stone-200 bg-white shadow-[0_25px_100px_rgba(0,0,0,0.20)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-title"
          >
            {/* Orange top accent */}
            <div className="h-1.5 bg-orange-500" />

            <div className="p-6 sm:p-7">
              {/* Header */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-orange-500">
                    {forgotStep === 1 ? (
                      <Mail size={20} />
                    ) : (
                      <KeyRound size={20} />
                    )}
                  </div>

                  <h3
                    id="forgot-password-title"
                    className="font-display text-2xl font-black tracking-[-0.025em] text-stone-950"
                  >
                    Reset your password
                  </h3>

                  <p className="mt-1.5 text-sm leading-6 text-stone-500">
                    {forgotStep === 1
                      ? "Enter your registered email and we'll send you a verification OTP."
                      : `Enter the OTP sent to ${forgotForm.email}`}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeForgotPassword}
                  disabled={forgotLoading}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Step indicator */}
              <div className="mt-6 flex items-center gap-2">
                <div
                  className={`h-1.5 flex-1 rounded-full ${
                    forgotStep >= 1 ? "bg-orange-500" : "bg-stone-200"
                  }`}
                />
                <div
                  className={`h-1.5 flex-1 rounded-full ${
                    forgotStep >= 2 ? "bg-orange-500" : "bg-stone-200"
                  }`}
                />
              </div>

              {/* Error */}
              {forgotError && (
                <div
                  className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600"
                  role="alert"
                >
                  {forgotError}
                </div>
              )}

              {/* STEP 1 */}
              {forgotStep === 1 && (
                <div className="mt-6 space-y-4">
                  <div>
                    <Label className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500">
                      Email address
                    </Label>

                    <input
                      type="text"
                      value={forgotForm.email}
                      onChange={(e) => {
                        updateForgotField("email", e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          sendForgotOtp();
                        }
                      }}
                      placeholder="restaurant@example.com"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      disabled={forgotLoading}
                      className="mt-2 h-12 text-stone-900 w-full rounded-xl border border-stone-200 bg-stone-50/60 px-4 text-sm outline-none transition-all focus:border-orange-400 focus:bg-white -focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={sendForgotOtp}
                    disabled={forgotLoading}
                    className="h-12 w-full rounded-xl bg-orange-500 text-sm font-bold text-white shadow-[0_8px_25px_rgba(249,115,22,0.22)] hover:bg-orange-600"
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={17} />
                        Sending OTP...
                      </>
                    ) : (
                      <>
                        Send OTP
                        <ArrowRight size={17} className="ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* STEP 2 */}
              {forgotStep === 2 && (
                <div className="mt-6 space-y-4">
                  <div>
                    <Label className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500">
                      Verification OTP
                    </Label>

                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={forgotForm.otp}
                      onChange={(e) =>
                        updateForgotField(
                          "otp",
                          e.target.value.replace(/\D/g, ""),
                        )
                      }
                      placeholder="Enter 6-digit OTP"
                      autoComplete="one-time-code"
                      autoFocus
                      disabled={forgotLoading}
                      className="mt-2 h-12 text-stone-900 rounded-xl border-stone-200 bg-stone-50/60 px-4 text-center text-lg font-bold tracking-[0.35em] focus:border-orange-400 focus:bg-white focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <Label className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500">
                      New password
                    </Label>

                    <Input
                      type="password"
                      value={forgotForm.password}
                      onChange={(e) =>
                        updateForgotField("password", e.target.value)
                      }
                      placeholder="Minimum 6 characters"
                      autoComplete="new-password"
                      disabled={forgotLoading}
                      className="mt-2 text-stone-900 h-12 rounded-xl border-stone-200 bg-stone-50/60 px-4 text-sm focus:border-orange-400 focus:bg-white focus:ring-orange-100"
                    />
                  </div>

                  <div>
                    <Label className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500">
                      Confirm password
                    </Label>

                    <Input
                      type="password"
                      value={forgotForm.confirmPassword}
                      onChange={(e) =>
                        updateForgotField("confirmPassword", e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          resetForgotPassword();
                        }
                      }}
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                      disabled={forgotLoading}
                      className="mt-2 text-stone-900 h-12 rounded-xl border-stone-200 bg-stone-50/60 px-4 text-sm focus:border-orange-400 focus:bg-white focus:ring-orange-100"
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={resetForgotPassword}
                    disabled={forgotLoading}
                    className="h-12 w-full rounded-xl bg-orange-500 text-sm font-bold text-white shadow-[0_8px_25px_rgba(249,115,22,0.22)] hover:bg-orange-600"
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={17} />
                        Updating password...
                      </>
                    ) : (
                      <>
                        Reset Password
                        <CheckCircle2 size={17} className="ml-2" />
                      </>
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={sendForgotOtp}
                    disabled={forgotLoading}
                    className="w-full text-center text-xs font-semibold text-orange-500 hover:text-orange-600 disabled:opacity-50"
                  >
                    Resend OTP
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (!forgotLoading) {
                        setForgotStep(1);
                        setForgotError("");
                      }
                    }}
                    disabled={forgotLoading}
                    className="w-full text-center text-xs font-medium text-stone-400 hover:text-stone-600 disabled:opacity-50"
                  >
                    Change email address
                  </button>
                </div>
              )}

              <div className="mt-6 border-t border-stone-100 pt-4 text-center text-[10px] text-stone-400">
                For security, OTPs expire automatically and can only be used
                once.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
